import React from 'react';
import { Controller, useFormContext, FieldArrayWithId } from 'react-hook-form';
import styled from 'styled-components';
import { STATE_CODES } from '~/constants';
import { colors } from '~/constants/colors';
import { smallerThanOrEqualTo } from '~/core/util/styledComponents';
import { isBookingWidgetAddressRequired } from '~/core/util/location';
import { useSolvSelector } from '~/reducers';
import { AdditionalPatientsFormState, getPatientFieldName } from '.';
import {
  ADDRESS_FIELD_NAME,
  ADDRESS_SECONDARY_FIELD_NAME,
  CITY_FIELD_NAME,
  STATE_CODE_FIELD_NAME,
  ZIP_CODE_FIELD_NAME,
} from '../BookingWidget/util/validators';
import CheckBoxField from './CheckBoxField';
import { FieldContainer } from './FieldContainer';
import PatientDifferentAddressLabel from './PatientDifferentAddressLabel';
import SelectField from './SelectField';
import TextField from './TextField';

interface HomeAddressInterface {
  index: number;
}

const HomeAddressContainer = styled.div.withConfig({ componentId: 'sc-kgyfpc-0' })`
  margin-top: 20px;
`;

const HasDifferentAddressContainer = styled.div.withConfig({ componentId: 'sc-kgyfpc-1' })`
  ${smallerThanOrEqualTo.md`
    margin-left: 16px;
  `}
`;

const HomeAddressHeading = styled.div.withConfig({ componentId: 'sc-kgyfpc-2' })`
  font-weight: 400;
  font-size: 15px;
  text-transform: uppercase;
  color: ${colors.grey600};
  margin-bottom: 10px;
  ${smallerThanOrEqualTo.md`
    margin-left: 16px;
    margin-top: 8px;
  `}
`;

const AddressSection = styled.div.withConfig({ componentId: 'sc-kgyfpc-3' })`
  ${FieldContainer} {
    &:last-child {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

const CitySateZipSection = styled.div.withConfig({ componentId: 'sc-kgyfpc-4' })`
  display: flex;
  ${FieldContainer} {
    border-radius: 0;
    flex: 1;
    border-bottom-width: 2px;

    &:first-child {
      border-right: none;
      border-top: none;
      flex: 1.5;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-bottom-right-radius: 4px;
      border-left: none;
    }

    select {
      width: 100%;
      border: none;
    }
  }
`;

const STATE_CODE_OPTIONS = STATE_CODES.map((stateCode) => ({
  key: stateCode.toUpperCase(),
  label: stateCode.toUpperCase(),
  value: stateCode.toUpperCase(),
}));

const HomeAddress = ({ index }: HomeAddressInterface) => {
  const { control, watch } = useFormContext<AdditionalPatientsFormState>();
  const bookingWidgetAddressRequired = useSolvSelector((state) =>
    isBookingWidgetAddressRequired(state.location)
  );
  const showHomeAddress = watch(getPatientFieldName(index, 'hasDifferentAddress') as any, false);

  if (!bookingWidgetAddressRequired) {
    return null;
  }

  return (
    <HomeAddressContainer>
      <HasDifferentAddressContainer>
        <Controller
          control={control}
          name={getPatientFieldName(index, 'hasDifferentAddress')}
          render={({ field, fieldState }) => (
            <CheckBoxField
              {...field}
              error={!!fieldState.error}
              label={<PatientDifferentAddressLabel />}
            />
          )}
        />
      </HasDifferentAddressContainer>
      {showHomeAddress && (
        <>
          <HomeAddressHeading>Home Address</HomeAddressHeading>
          <AddressSection>
            <Controller
              control={control}
              name={getPatientFieldName(index, ADDRESS_FIELD_NAME)}
              render={({ field, fieldState }) => (
                <TextField {...field} error={!!fieldState.error} placeholder="Street Address" />
              )}
              rules={{
                required: true,
              }}
            />
            <Controller
              control={control}
              name={getPatientFieldName(index, ADDRESS_SECONDARY_FIELD_NAME)}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  error={!!fieldState.error}
                  placeholder="Apt, Suite, etc. (optional)"
                />
              )}
              rules={{
                required: false,
              }}
            />
          </AddressSection>

          <CitySateZipSection>
            <Controller
              control={control}
              name={getPatientFieldName(index, CITY_FIELD_NAME)}
              render={({ field, fieldState }) => (
                <TextField {...field} error={!!fieldState.error} placeholder="City" />
              )}
              rules={{
                required: true,
              }}
            />
            <Controller
              control={control}
              name={getPatientFieldName(index, STATE_CODE_FIELD_NAME)}
              render={({ field, fieldState }) => (
                <SelectField
                  {...field}
                  error={!!fieldState.error}
                  options={STATE_CODE_OPTIONS}
                  placeholder="State"
                />
              )}
              rules={{
                required: true,
              }}
            />
            <Controller
              control={control}
              name={getPatientFieldName(index, ZIP_CODE_FIELD_NAME)}
              render={({ field, fieldState }) => (
                <TextField {...field} error={!!fieldState.error} placeholder="Zip code" />
              )}
              rules={{
                required: true,
              }}
            />
          </CitySateZipSection>
        </>
      )}
    </HomeAddressContainer>
  );
};

export default HomeAddress;
